import React, { useState } from 'react';
import Box from '@components/Atoms/Box';
import { IconButton, ListItemText, MenuItem, Tooltip, Typography } from '@mui/material';
import ConstructionIcon from '@mui/icons-material/Construction';
import CompareIcon from '@mui/icons-material/Compare';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Dialog from '@components/Atoms/Dialog';
import { STATE } from './SlideControls';

interface DraftMenuItemProps {
  state: string;
  onMenuClick: () => void;
  handleSave: () => void;
  handleCompare: () => void;
  handleEdit: () => void;
}

const DraftMenuItem: React.FC<DraftMenuItemProps> = ({
  state,
  onMenuClick,
  handleSave,
  handleCompare,
  handleEdit,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  return (
    <>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Override current saved slide?"
        content="You can create a draft by clicking 'Create Draft' in the menu."
        actions={[
          {
            label: 'Cancel',
            onClick: () => setConfirmOpen(false),
          },
          {
            label: 'Save Draft',
            onClick: () => {
              setConfirmOpen(false);
              handleSave();
            },
          },
        ]}
      >
        <Typography>
          Are you sure you want to overwrite the current saved slide with your draft
          version?
          <br />
          <Typography
            component="p"
            sx={{
              fontWeight: 'bold',
            }}
          >
            This action cannot be undone.
          </Typography>
        </Typography>
      </Dialog>
      <MenuItem onClick={onMenuClick}>
        <ConstructionIcon sx={{ mr: 1 }} />
        <ListItemText
          primary="My Version (Draft)"
          secondary="Your copy, which you can edit and save."
          secondaryTypographyProps={{
            variant: 'caption',
            color: 'text.secondary',
            fontSize: '0.75rem',
          }}
        />
        <Box sx={{ display: 'flex', gap: '0.25rem', ml: '1rem' }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEdit()}
              aria-label="Edit"
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Save">
            <IconButton
              size="small"
              onClick={(e) => {
                if (state === STATE.SAVED_COPY) {
                  setConfirmOpen(true);
                } else {
                  handleSave();
                }
                e.stopPropagation();
              }}
              aria-label="Save"
              sx={{
                backgroundColor: 'secondary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'secondary.dark',
                },
              }}
            >
              <SaveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Compare with the slide above">
            <IconButton
              size="small"
              onClick={() => handleCompare()}
              aria-label="Compare"
              sx={{
                backgroundColor: 'info.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'info.dark',
                },
              }}
            >
              <CompareIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </MenuItem>
    </>
  );
};

export default DraftMenuItem;
